import { FontAwesome5 } from "@expo/vector-icons";
import { useMutation } from "@tanstack/react-query";
import { CheckBox, Text } from "@ui-kitten/components";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import ConfirmPurchaseButton from "components/ConfirmPurchaseButton";
import EventBanner from "components/EventBanner";
import FanRallyTOS from "components/FanRallyTOSDialog/FanRallyTOS";
import FanRallyTOSDialog from "components/FanRallyTOSDialog/FanRallyTOSDialog";
import MainContainer from "components/MainContainer";
import Spacer from "components/Spacer";
import { resetPremiumInventory } from "features/slices/reservationFlowSlice";
import {
  AccountTicketDetails,
  EventNoticeDetails,
  PlanPackageComponentDetails,
  PremiumCodeDetails,
  SubscriptionDetails,
  TicketDetails,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useCurrentUser from "hooks/useCurrentUser";
import useIsRunningInBallpark from "hooks/useIsRunningInBallpark";
import useMarketingAssetsSections from "hooks/useMarketingAssetsSections";
import { uniqBy } from "lodash";
import { ReserveScreenProps } from "navigation/types";
import i18n from "plugins/localization";
import { updateAccountFetcher } from "queries/account.queries";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import {
  ReserveOrRedeemItem,
  calculateChargeRequest,
  calculateReservationUpdateChargesRequest,
  reserveForReservationUpdateRequest,
  reserveTicketsFetcher,
} from "queries/ticket.queries";
import React, { useEffect, useMemo, useState } from "react";
import { Platform, Pressable, View } from "react-native";
import PaymentMethodCard from "screens/Account/components/PaymentMethodCard";
import VerifyPhonePopup from "screens/Dashboard/components/VerifyPhonePopup";
import { formatEventDate } from "utils/eventUtils";
import { toEventDetail } from "utils/navigationUtils";
import { getPlanType } from "utils/planPackageUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { getMaxSeatsAllowedPerReservation } from "utils/tenantUtils";
import { barcodeHoursBefore } from "utils/ticketUtils";
import toast from "utils/toast";
import CartSummary from "./components/CartSummary";
import HoldDetails, { FanRallyMarketPlace } from "./components/HoldDetails";
import ImportantMessageDialog from "./components/ImportantMessageDialog";
import PremiumHoldDetails from "./components/PremiumHoldDetails";
import RefundDetails from "./components/RefundDetails";
import {
  findComponentById,
  findUserById,
  getRemainingAvailableSlotsForComponent,
  getRemainingPerEventMaxForComponent,
  getRemainingQuantityForComponent,
  isPrimaryComponent,
  sortSubscriptionsByPrimaryMember,
  usersNotGoing,
} from "./helpers";

// checkout page for:
//  Standard flow: RESERVATION, VOUCHER and BANK
//  Preimum flow: SUITE
// If the premiumCode and premiumClass(set in PremiumCodeDetails screen) aren't null, then use the premium flow

export const getNewlyAddedEvent = (
  newTickets: TicketDetails[],
  accountEvents: AccountTicketDetails[]
): AccountTicketDetails => {
  const newTicket = newTickets[0];
  return accountEvents.find((accEvent) =>
    accEvent.tickets.some((ticket) => ticket.id === newTicket.id)
  )!;
};

const Reserve: React.FC<ReserveScreenProps> = () => {
  // redux
  const {
    selectedEvent,
    selectedSubscription,
    originalHeldDetail,
    actualHeldDetail,
    premiumInventory,
    selectedPremiumCode,
    isChangingSeats,
    originalEventId,
    originalTickets,
  } = useAppSelector((state) => state.reservationFlow);

  const dispatch = useAppDispatch();

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const barcodeProvider = tenantConfig?.barcodeProvider;

  const isPremiumFlow = premiumInventory !== null;

  // custom hooks
  const {
    account,
    accountEvents: events,
    fetchAccount,
    isFetching: fetchingAccount,
  } = useAccount();
  const { isRunningInBallpark } = useIsRunningInBallpark();
  const { currentUser } = useCurrentUser();

  const { isPremiumInventoryMarketingAssets } = useMarketingAssetsSections(
    selectedSubscription?.planPackage.id
  );
  // catch cases when url is directly accessed
  if (!selectedEvent || !selectedSubscription || !account) {
    return null;
  }

  // local states
  const [matchedSubscriptions, setMatchedSubscriptions] = useState<
    SubscriptionDetails[]
  >([]);
  const [selectedUsersAndComponents, setSelectedUsersAndComponents] = useState<
    number[]
  >([]);
  const [notices, setNotices] = useState<EventNoticeDetails[]>([]);
  const [importantMessageDialog, setImportantMessageDialog] = useState(false);
  const [TOSDialog, setTOSDialog] = useState(false);
  const [TOSChecked, setTOSChecked] = useState(false);
  const [verifyPhonePopupVisible, setVerifyPhonePopupVisible] = useState(false);

  // react queries
  const { mutateAsync: updateAccount, isLoading: updatingAccount } =
    useMutation(updateAccountFetcher);
  const { mutateAsync: reserve, isLoading: reserving } = useMutation(
    reserveTicketsFetcher
  );
  const { mutateAsync: reserveForReservationUpdate, isLoading: updating } =
    useMutation(reserveForReservationUpdateRequest);
  // helper function for useCalculateChargeQuery`

  const findPrimaryComponentId = () => {
    return selectedSubscription.planPackage.components.find(
      (component) => component.primary
    )!.id;
  };

  const getCalculateChargeItems = () => {
    return actualHeldDetail.map((ticket, index) => {
      const user = findUserById(
        selectedUsersAndComponents[index],
        matchedSubscriptions
      );
      const planPackageComponentId =
        (!isPremiumFlow && user) || isPremiumFlow
          ? findPrimaryComponentId()
          : selectedUsersAndComponents[index];

      return {
        planPackageId: ticket.planPackageId,
        // in the selectedUsersAndComponents array,
        // if the id in the given index is a user id,
        // then we should use the primary plan package component's id
        // otherwise if it is a component id, then we should use it instead
        planPackageComponentId,
        ticketId: ticket.id,
      };
    });
  };

  const handleCalculateCharge = () => {
    calculateCharge({
      accountId: account.id,
      eventId: selectedEvent.eventId,
      items: getCalculateChargeItems(),
    });
  };

  const handleCalculateReservationUpdateChange = () => {
    calculateReservationUpdateCharge({
      accountId: account.id,
      eventId: selectedEvent.eventId,
      newTicketItems: getCalculateChargeItems(),
      ticketRefundIds: originalTickets.map((t) => t.id),
    });
  };

  const { data: standardReservationCharge, mutateAsync: calculateCharge } =
    useMutation(calculateChargeRequest);

  const {
    data: reservationUpdateCharge,
    mutateAsync: calculateReservationUpdateCharge,
  } = useMutation(calculateReservationUpdateChargesRequest);

  // Normalize charge details
  const charge = isChangingSeats
    ? reservationUpdateCharge?.chargeDetails
    : standardReservationCharge;

  // helper functions and variables
  const heldTicket = actualHeldDetail[0];
  const planType = getPlanType(selectedSubscription.planPackage);

  // renders the price details for premium seating, type === 'GA' only
  const renderPremiumLocationSubtitle = (
    premiumCode: PremiumCodeDetails | null
  ) => {
    if (
      !premiumCode ||
      !charge ||
      premiumInventory?.premiumClass.type === "SEATS"
    )
      return null;

    return (
      <View>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Text category="s1">Quantity: {actualHeldDetail.length}</Text>
        </View>
      </View>
    );
  };

  const handleFRTOSClick = () => {
    if (isRunningInBallpark || Platform.OS !== "web") {
      setTOSDialog(true);
    } else {
      const wd = window.open()!;
      wd.document.write(FanRallyTOS);
      wd.document.title = "FanRally Marketplace Terms of Service";
    }
  };

  const hasOriginalHeldSections = useMemo(() => {
    if (heldTicket.seat.ga) {
      // for GA, only check if the section is the same
      return originalHeldDetail.section === heldTicket.seat.section;
    }
    return (
      originalHeldDetail.section === heldTicket.seat.section &&
      originalHeldDetail.row === heldTicket.seat.row
    );
  }, [heldTicket]);

  // Subscriptions in account associated with the selected plan package
  const planPackageSubscriptions = account.subscriptions.filter(
    (sub) =>
      filterIneligibleSubscriptions(sub) &&
      sub.planPackage.id === selectedSubscription.planPackage.id
  );

  const findAndSetMatchedSubscriptions = async () => {
    if (planType === "RESERVATION") {
      const usersNotGoingIdSet = usersNotGoing({
        subscriptions: planPackageSubscriptions,
        eventId: selectedEvent.eventId,
        events,
        isChangingSeats,
        groupCode: isChangingSeats ? originalTickets[0]?.groupCode : undefined,
      });
      // find all users that are eligible to this event
      const eligibleUsers: SubscriptionDetails[] = [];
      planPackageSubscriptions.forEach((sub) => {
        if (!usersNotGoingIdSet.has(sub.member.user.id)) {
          eligibleUsers.push(sub);
        }
      });
      setMatchedSubscriptions(eligibleUsers);
    } else {
      setMatchedSubscriptions(planPackageSubscriptions);
    }
  };

  const initImportantMessages = () => {
    const notices = heldTicket.event.notices.filter((notice) =>
      notice.displayTypes.includes("CONFIRMATION")
    );
    if (notices.length === 0) return;
    setNotices(notices);
    setImportantMessageDialog(true);
  };

  // Triggered when the user selector changes or initializing
  const addSelectedUserOrComponent = (
    id: number /*could be user id or component id*/,
    initialIndex: number
  ) => {
    const user = findUserById(id, matchedSubscriptions);
    const component = findComponentById(id, matchedSubscriptions);
    let newSelectedUsersOrComponents: any = [];
    const newId = user ? user.id : component!.id;
    setSelectedUsersAndComponents((oldSelectedUsersOrComponents) => {
      newSelectedUsersOrComponents = [...oldSelectedUsersOrComponents];
      newSelectedUsersOrComponents[initialIndex] = newId;
      return newSelectedUsersOrComponents;
    });
  };

  const getStandardFlowReserveOrRedeemItems = () => {
    const result: ReserveOrRedeemItem[] = [];
    selectedUsersAndComponents.forEach((userOrComponent) => {
      const user = findUserById(userOrComponent, matchedSubscriptions);
      if (user) {
        result.push({
          userId: userOrComponent,
          planPackageComponentId: findPrimaryComponentId(),
        });
      } else {
        if (
          isPrimaryComponent(userOrComponent, selectedSubscription) &&
          getPlanType(selectedSubscription.planPackage) !== "VOUCHER"
        ) {
          result.push({
            planPackageComponentId: userOrComponent,
            userId: selectedSubscription.member.user.id,
          });
          return;
        }
        result.push({
          planPackageComponentId: userOrComponent,
        });
      }
    });
    return result;
  };

  const getPremiumFlowReserveOrRedeemItems = () => {
    const result: ReserveOrRedeemItem[] = [];
    for (let i = 0; i < actualHeldDetail.length; i++) {
      result.push({
        planPackageComponentId:
          selectedSubscription.planPackage.primaryComponent.id,
        userId: selectedSubscription.member.user.id,
      });
    }
    return result;
  };
  const getReserveOrRedeemItems = () => {
    if (isPremiumFlow) {
      return getPremiumFlowReserveOrRedeemItems();
    } else {
      return getStandardFlowReserveOrRedeemItems();
    }
  };

  const handleSubmitButtonPress = () => {
    if (isChangingSeats) {
      handleChangeSeats();
    } else {
      handleReserveOrRedeem();
    }
  };

  const shouldShowPriceDetails = useMemo(() => {
    if (selectedEvent.eventType === "NIL") return false;
    return charge && !isChangingSeats;
  }, [charge, isChangingSeats, selectedEvent]);

  const handleChangeSeats = async () => {
    const reservedTickets = await reserveForReservationUpdate({
      accountId: account!.id,
      planPackageId: selectedSubscription!.planPackage.id,
      eventId: selectedEvent!.eventId,
      groupCode: originalTickets[0].groupCode!,
      items: getReserveOrRedeemItems(),
      releaseTicketIds: originalTickets.map((t) => t.id),
    });
    await postReservationComplete(reservedTickets);
    toast.success("Congratulations! Your reservation has been updated!");
  };

  const handleReserveOrRedeem = async () => {
    const seatPreference = account.seatPreference;
    const params = {
      eventId: selectedEvent.eventId,
      accountId: account.id,
      section: heldTicket.seat.section,
      row: heldTicket.seat.row,
      planPackageId: selectedSubscription.planPackage.id,
    };
    const tickets = await reserve({
      ...params,
      items: getReserveOrRedeemItems(),
    });
    // Update account marketplace flag if needed
    if (account.marketplace === false && Number(charge?.totalPrice) > 0) {
      await updateAccount({
        marketplace: true,
        seatPreference,
      });
    }
    await postReservationComplete(tickets);
    toast.success(i18n.t("common.reservationConfirmedMessage"));
  };

  const postReservationComplete = async (tickets: TicketDetails[]) => {
    const accountBFF = await fetchAccount(); // fetch updated account info
    const newlyAddedEvent = getNewlyAddedEvent(
      tickets,
      accountBFF.data!.events!
    );
    toEventDetail(newlyAddedEvent.groupCode.groupCode);
  };

  const marketPlaceNotOptIn = () => {
    if (charge === undefined) return false;
    if (
      parseFloat(charge?.totalPrice) > 0 &&
      TOSChecked === false &&
      account.marketplace === false
    ) {
      return true;
    }
    return false;
  };

  // array of available guest components
  const guestComponents =
    matchedSubscriptions[0]?.planPackage.components.filter((component) =>
      component.type.includes("GUEST")
    ) || [];

  const findGuestComponentsByType = (
    matchedSubscriptions: SubscriptionDetails[],
    type: "PAID" | "FREE"
  ) => {
    return matchedSubscriptions.reduce(
      (guestComponents: PlanPackageComponentDetails[], sub) => {
        let result: PlanPackageComponentDetails[] = [];
        for (const component of sub.planPackage.components) {
          if (component.type.includes("GUEST")) {
            if (
              (type === "FREE" && component.name.includes("Included")) ||
              (type === "PAID" && component.name.includes("Paid"))
            ) {
              result.push(component);
            }
          }
        }
        return [...guestComponents, ...result];
      },
      []
    );
  };

  // will produce an array of user ids and guest component ids which indicates the order of selectors,
  // for example: [member1,member2,freeGuest1,freeGuest2,paidGuest1,paidGuest2]
  const prefillSelectorArray = useMemo(() => {
    if (matchedSubscriptions.length === 0) return [];
    const result: number[] = [];
    const maxSeatsAllowed = getMaxSeatsAllowedPerReservation(tenantConfig);
    const getRemainingQuantityForComponentParams = {
      subscriptions: planPackageSubscriptions,
      events: events || [],
      isChangingSeats,
      originalEventId,
      eventId: selectedEvent.eventId,
      maxSeatsAllowed,
    };
    let freeGuestComponents = findGuestComponentsByType(
      matchedSubscriptions,
      "FREE"
    ).filter(
      (component) =>
        getRemainingQuantityForComponent({
          component,
          ...getRemainingQuantityForComponentParams,
        }) > 0
    );
    let paidGuestComponents = findGuestComponentsByType(
      matchedSubscriptions,
      "PAID"
    ).filter(
      (component) =>
        getRemainingQuantityForComponent({
          component,
          ...getRemainingQuantityForComponentParams,
        }) > 0
    );

    const primaryComponent =
      planPackageSubscriptions[0].planPackage.primaryComponent;

    if (planType === "RESERVATION") {
      // Sort matched subscriptions so that primary member's subscriptions appear first
      // This ensures the primary member appears first in the selectors
      sortSubscriptionsByPrimaryMember(matchedSubscriptions);
      matchedSubscriptions.forEach((sub) => {
        result.push(sub.member.user.id);
      });
    } else if (planType === "VOUCHER") {
      const maxVouchers = getRemainingAvailableSlotsForComponent({
        component: primaryComponent,
        subscriptions: planPackageSubscriptions,
        events,
        eventId: selectedEvent.eventId,
        isChangingSeats,
        originalEventId,
        maxSeatsAllowed,
      });

      let counter = 0;
      const primaryVoucherComponentId = primaryComponent.id;

      while (counter < maxVouchers && counter < actualHeldDetail.length) {
        result.push(primaryVoucherComponentId);
        counter++;
      }
    } else {
      // plan type is BANK
      let counter = 0;

      const availableBankSlotsForEvent = getRemainingPerEventMaxForComponent({
        component: planPackageSubscriptions[0].planPackage.primaryComponent,
        subscriptions: planPackageSubscriptions,
        events,
        eventId: selectedEvent.eventId,
        isChangingSeats,
        maxSeatsAllowed,
      });

      while (
        counter < availableBankSlotsForEvent &&
        counter < actualHeldDetail.length
      ) {
        result.push(primaryComponent.id);
        counter++;
      }
    }

    // remove duplicated guest components
    const mergedUniqGuestComponents = uniqBy(
      [...freeGuestComponents, ...paidGuestComponents],
      "id"
    );

    // Make sure free guest components appear first in the selector array
    mergedUniqGuestComponents.sort((component) => {
      if (component.name.includes("Included")) {
        return -1;
      }
      return 1;
    });

    const selectedPlanSubscriptions = account.subscriptions.filter(
      (sub) => sub.planPackage.id === selectedSubscription.planPackage.id
    );

    for (const guestComponent of mergedUniqGuestComponents) {
      const perEventMaxForComponent = getRemainingPerEventMaxForComponent({
        component: guestComponent,
        subscriptions: selectedPlanSubscriptions,
        events,
        eventId: selectedEvent.eventId,
        isChangingSeats,
        maxSeatsAllowed,
      });
      if (perEventMaxForComponent === maxSeatsAllowed) {
        // if component has unlimited guest pass amount, fill the result array with its component id
        while (result.length < actualHeldDetail.length) {
          result.push(guestComponent.id);
        }
        break;
      } else {
        // if component has a fixed amount of guest pass, such as 3, push 3 X its component id to the result array
        let counter = 0;
        while (
          counter < perEventMaxForComponent &&
          result.length < actualHeldDetail.length &&
          result.length < maxSeatsAllowed
        ) {
          result.push(guestComponent.id);
          counter++;
        }
      }
    }
    return result;
  }, [matchedSubscriptions]);

  const confirmPurchaseButtonText = useMemo(() => {
    if (!charge) return "";
    if (isChangingSeats) return "Update Reservation";
    const { totalCreditCardCharges, totalPoints } = charge!;
    const totalCharge = Number(totalCreditCardCharges);
    if (totalCharge === 0) {
      if (planType === "BANK") {
        // if payment is covered by bank balance, show Purchase with Bank,
        // if no payment required, show Reserve
        return Number(totalPoints) > 0
          ? i18n.t("common.purchaseAndBook")
          : i18n.t("common.book");
      } else {
        // plan type is RESERVATION or Voucher
        return planType === "RESERVATION"
          ? i18n.t("common.book")
          : i18n.t("common.redeem");
      }
    } else {
      // total charge is greater than 0
      return planType === "VOUCHER"
        ? i18n.t("common.purchaseAndRedeem")
        : i18n.t("common.purchaseAndBook");
    }
  }, [charge, planType]);

  // For marketing assets inventory, we don't want to show "X seats on hold" text
  const bannerText = useMemo(() => {
    if (
      premiumInventory &&
      isPremiumInventoryMarketingAssets(premiumInventory)
    ) {
      return (
        premiumInventory.premiumClass.name || premiumInventory.premiumClass.code
      );
    }
    return undefined;
  }, [premiumInventory]);

  // standard checkout flow
  useEffect(() => {
    if (selectedUsersAndComponents.length !== actualHeldDetail.length) return;
    if (isChangingSeats) {
      handleCalculateReservationUpdateChange();
    } else {
      handleCalculateCharge();
    }
  }, [selectedUsersAndComponents]);

  useEffect(() => {
    if (premiumInventory !== null) {
      handleCalculateCharge();
    }
  }, [premiumInventory]);

  useEffect(() => {
    initImportantMessages();
    findAndSetMatchedSubscriptions();

    // When leaving the page, set premium inventory to null
    return () => {
      dispatch(resetPremiumInventory());
    };
  }, []);

  const isLoading = reserving || updating || updatingAccount || fetchingAccount;
  return (
    <MainContainer grayBackground>
      <VerifyPhonePopup
        visible={verifyPhonePopupVisible}
        onCancel={() => setVerifyPhonePopupVisible(false)}
      />
      <ImportantMessageDialog
        notices={notices}
        visible={importantMessageDialog}
        setVisible={setImportantMessageDialog}
      />
      <FanRallyTOSDialog visible={TOSDialog} setVisible={setTOSDialog} />
      <View style={{ padding: 15 }}>
        <EventBanner
          event={actualHeldDetail[0].event}
          type="RESERVE"
          heldTicketLength={actualHeldDetail.length}
          bannerText={bannerText}
        />

        <View className="mt-2">
          {!currentUser?.phoneVerified && (
            <View className="flex-row">
              <Text category="p1" status="danger">
                Phone number not verified, {""}
              </Text>
              <Pressable
                className="flex-row"
                onPress={() => setVerifyPhonePopupVisible(true)}
              >
                <Text status="info">Click here </Text>
              </Pressable>
              <Text status="danger">to verify your phone number</Text>
            </View>
          )}
          {!hasOriginalHeldSections && (
            <Text category="p1" status="danger">
              {i18n.t("screens.Reserve.seatsAlreadyTaken")}
            </Text>
          )}
        </View>

        <Spacer />

        {/* premium checkout flow */}
        {isPremiumFlow && (
          <BaseCard title="Location" style={{ marginBottom: 8 }}>
            <BaseCardListItem
              title={premiumInventory.premiumClass.name}
              showArrow={false}
            >
              {renderPremiumLocationSubtitle(selectedPremiumCode)}
            </BaseCardListItem>
          </BaseCard>
        )}
        {isPremiumFlow && premiumInventory.premiumClass.type === "SEATS" && (
          <>
            {actualHeldDetail.map((detail, index) => (
              <PremiumHoldDetails
                key={detail.id}
                ticket={detail}
                charge={charge}
                index={index}
              ></PremiumHoldDetails>
            ))}
          </>
        )}
        {isChangingSeats && (
          <View>
            <Text category="h6">
              {`Existing Reservation: ${
                originalTickets[0].event.name
              } ${formatEventDate(originalTickets[0].event)}`}
            </Text>
            <Spacer />
            <BaseCard>
              <Text>
                {originalTickets[0].seat.ga
                  ? i18n.t("common.generalAdmission")
                  : `${i18n.t("common.section")}: ${
                      originalTickets[0].seat.sectionDisplay
                    } ` +
                    `${i18n.t("common.row")}: ${
                      originalTickets[0].seat.rowDisplay
                    } `}
              </Text>
              <BaseCardListItem
                showArrow={false}
                title={() => {
                  return (
                    <Text>
                      {`${i18n.t("common.seat", {
                        count: 1,
                      })}: ${originalTickets
                        .map((ticket) => ticket.seat.seatDisplay)
                        .join(", ")}`}
                    </Text>
                  );
                }}
              />
            </BaseCard>
            {/* {reservationUpdateCharge && (
              <Text style={{ marginTop: 8 }}>
                Total Cost:{" "}
                {formatCurrency(
                  reservationUpdateCharge.refundDetails.totalPrice,
                  {
                    priceType: reservationUpdateCharge.refundDetails.priceType,
                  }
                )}
              </Text>
            )} */}
            {reservationUpdateCharge && (
              <>
                <Spacer />
                <RefundDetails
                  refundDetails={reservationUpdateCharge.refundDetails}
                />
              </>
            )}
          </View>
        )}
        <Spacer />
        {/* standard checkout flow */}
        {!isPremiumFlow && matchedSubscriptions.length > 0 && (
          <>
            {isChangingSeats && (
              <View>
                <Text category="h6">New Reservation: </Text>
                <Spacer />
              </View>
            )}
            {actualHeldDetail.map((detail, index) => (
              <HoldDetails
                ticket={detail}
                key={detail.id}
                planType={planType}
                charge={charge}
                matchedSubscriptions={matchedSubscriptions}
                initialIndex={index}
                selectedUsersAndComponents={selectedUsersAndComponents}
                addSelectedUserOrComponent={addSelectedUserOrComponent}
                guestComponents={guestComponents}
                prefillSelectorArray={prefillSelectorArray}
                selectedSubscription={selectedSubscription}
              />
            ))}
          </>
        )}

        {charge &&
          reservationUpdateCharge?.refundDetails &&
          isChangingSeats && (
            <Spacer>
              <CartSummary charge={charge} />
            </Spacer>
          )}

        {!charge && (
          <Text category="h4" style={{ marginVertical: 8 }}>
            Loading charge details...
          </Text>
        )}

        {/* only show payment method card if account has a credit card on file */}
        {Boolean(account.billingLast4) &&
          Number(charge?.totalCreditCardCharges) > 0 && (
            <View style={{ marginBottom: 8 }}>
              <PaymentMethodCard
                title={i18n.t("screens.Reserve.paymentDetails")}
              />
            </View>
          )}

        {shouldShowPriceDetails && <CartSummary charge={charge!} />}
        {/** <!-- We need to update the UI, so that if the "marketplace" flag is false, 
        and a user attempts to reserve a ticket with associated charges,
        then we prompt the user to opt into having their account data used in the FanRally marketplace.
        https://github.com/FanRally/fanrally-core/issues/506
         --> */}
        {charge &&
          parseFloat(charge.totalPrice) > 0 &&
          account.marketplace === false && (
            <View style={{ marginTop: 20 }}>
              <View
                style={{
                  marginTop: 15,
                  flexDirection: "row",
                  paddingHorizontal: 10,
                }}
              >
                <CheckBox
                  onChange={setTOSChecked}
                  checked={TOSChecked}
                  testID="TOS-checkbox"
                ></CheckBox>
                <Text style={{ textAlign: "left", marginLeft: 15 }}>
                  By clicking {confirmPurchaseButtonText}, you acknowledge that
                  you have read and agree to our{" "}
                  <Text
                    onPress={handleFRTOSClick}
                    style={{
                      color: "rgb(36, 152, 243)",
                      fontSize: 12,
                      fontWeight: "700",
                    }}
                  >
                    FanRally Marketplace Terms of Service
                  </Text>
                </Text>
              </View>
            </View>
          )}

        {!isChangingSeats && Number(charge?.totalCreditCardCharges) > 0 && (
          <View style={{ marginVertical: 16 }}>
            <FanRallyMarketPlace
              name={i18n.t("screens.Reserve.poweredByFanRally")}
            />
          </View>
        )}

        {charge ? (
          <View style={{ marginTop: 30 }}>
            <ConfirmPurchaseButton
              charge={Number(charge.totalCreditCardCharges)}
              buttonText={confirmPurchaseButtonText}
              loading={isLoading}
              disabled={isLoading || marketPlaceNotOptIn()}
              onPress={handleSubmitButtonPress}
            />
          </View>
        ) : null}

        {barcodeProvider !== "AXS" && (
          <View style={{ flexDirection: "row", marginTop: 20 }}>
            <FontAwesome5
              name="calendar-alt"
              size={24}
              color="rgb(117, 117, 117)"
            />
            <Text style={{ marginHorizontal: 15 }}>
              {i18n.t("screens.Reserve.barcodesDeliveredWindow", {
                hours: barcodeHoursBefore(heldTicket.barcodeMinutesBefore),
              })}
            </Text>
          </View>
        )}
      </View>
    </MainContainer>
  );
};

export default Reserve;
